import { NotificationType } from '@routific/hive';
import { pruneFilterState } from '@routific/hive/v5';
import type { DateTime } from 'luxon';
import { flow, IAnyStateTreeNode, types } from 'mobx-state-tree';
import i18n from '../../i18n';
import { getGraphQLErrorCode } from '../../helpers/getGraphQLErrorCode';
import getAppEnv from './utils/getAppEnv';
import getAppRoot from './utils/getAppRoot';
import AnalyticsEvents from '../../analytics/analyticsEvents';
import {
  selectFromImportShopifyOrdersResultDto,
  selectFromShopifyAvailableFieldDto,
  selectFromShopifyConfigurationDto,
  ShopifyDeliveryMethodFilter,
  selectFromShopifyFieldMappingDto,
  ShopifyFulfillmentStatusFilter,
  selectFromShopifyInstallationDto,
  selectFromShopifyOrderDto,
  selectFromShopifyOrdersPageInfoDto,
  selectFromShopifyOrdersResultDto,
  ShopifyPaymentStatusFilter,
  selectFromSkippedShopifyOrderDto,
  selectFromCustomFieldPreferencesDto,
} from './models';
import {
  getShopifyImportLoadingMessageKey,
  shopifyImportAllSkippedNotification,
  shopifyImportConnectionRevokedNotification,
  shopifyImportErrorNotification,
  shopifyImportLoadingMessage,
  shopifyImportPartialSuccessNotification,
  shopifyImportRateLimitedNotification,
  shopifyImportSuccessNotification,
} from '../../notifications/shopify/importOrders';
import { CreationSource } from './models/CreationSourceEnum';

export interface ShopifyFieldMapping {
  routificField: string;
  shopifyFields: string[];
  isCustom?: boolean;
  label?: string;
}

type ShopifyFieldMappingMeta = Pick<ShopifyFieldMapping, 'isCustom' | 'label'>;

export interface ShopifyAvailableField {
  category: string;
  path: string;
  label: string;
}

export interface ShopifyOrder {
  id: string;
  name: string;
  createdAt: string;
  customerName?: string | null;
  shippingAddress?: string | null;
  deliveryMethod?: string | null;
  paymentStatus: string;
  fulfillmentStatus: string;
  tags: string[];
  isSynced: boolean;
}

export interface ShopifyOrdersPageInfo {
  hasNextPage: boolean;
  endCursor?: string | null;
}

export type ShopifyOrdersFilterState = {
  tags?: string[];
  fulfillmentStatuses?: ShopifyFulfillmentStatusFilter[];
  paymentStatuses?: ShopifyPaymentStatusFilter[];
  deliveryMethods?: ShopifyDeliveryMethodFilter[];
  createdAt?: [DateTime | null, DateTime | null];
};

export interface SkippedShopifyOrder {
  shopifyOrderId: string;
  reason: string;
}

export interface ShopifyImportResult {
  createdCount: number;
  skippedCount: number;
  skippedOrders: SkippedShopifyOrder[];
}

const createDefaultShopifyOrdersPageInfo = (): ShopifyOrdersPageInfo => ({
  hasNextPage: false,
  endCursor: null,
});

export type AddLocalFieldMappingResult =
  | { ok: true }
  | { ok: false; reason: 'duplicate' };

type GraphQLErrorWithCode = {
  response?: {
    errors?: Array<{
      message?: string;
      extensions?: {
        code?: unknown;
      };
    }>;
  };
};

const ShopifyInstallationQuery = selectFromShopifyInstallationDto()
  .uuid
  .shopDomain
  .active
  .enabledBy
  .createdAt
  .toString();

const ShopifyConfigurationSelector = selectFromShopifyConfigurationDto()
  .uuid
  .organizationUuid
  .tagDeliveredOrders
  .updateFulfillmentOnDelivery
  .createdAt
  .updatedAt
  .fieldMappings(
    selectFromShopifyFieldMappingDto()
      .routificField
      .shopifyFields
      .isCustom
      .label,
  )
  .toString();

const ShopifyAvailableFieldsSelector = selectFromShopifyAvailableFieldDto()
  .category
  .path
  .label
  .toString();

const ShopifyOrdersQuery = selectFromShopifyOrdersResultDto()
  .orders(
    selectFromShopifyOrderDto()
      .id
      .name
      .createdAt
      .customerName
      .shippingAddress
      .deliveryMethod
      .paymentStatus
      .fulfillmentStatus
      .tags
      .isSynced,
  )
  .pageInfo(selectFromShopifyOrdersPageInfoDto().hasNextPage.endCursor)
  .toString();

const ImportShopifyOrdersMutation = selectFromImportShopifyOrdersResultDto()
  .createdCount
  .skippedCount
  .skippedOrders(selectFromSkippedShopifyOrderDto().shopifyOrderId.reason)
  .toString();

const SHOPIFY_ORDERS_PAGE_SIZE = 50;
const FIELD_MAPPINGS_TRANSLATION_PREFIX =
  'shopify:fieldMappings';
const EMPTY_SHOPIFY_ORDERS_FILTERS: ShopifyOrdersFilterState = {};

const mapFieldMappings = (
  fieldMappings?: ShopifyFieldMapping[],
): ShopifyFieldMapping[] =>
  (fieldMappings ?? []).map((mapping) => ({
    routificField: mapping.routificField,
    shopifyFields: mapping.shopifyFields ?? [],
    isCustom: mapping.isCustom,
    label: mapping.label,
  }));

const cloneFieldMappings = (
  fieldMappings: ShopifyFieldMapping[],
): ShopifyFieldMapping[] =>
  fieldMappings.map((mapping) => ({
    routificField: mapping.routificField,
    shopifyFields: [...mapping.shopifyFields],
    isCustom: mapping.isCustom,
    label: mapping.label,
  }));

const getGraphQLErrorMessage = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || error === null) {
    return undefined;
  }

  const message =
    (error as GraphQLErrorWithCode).response?.errors?.[0]?.message;

  return typeof message === 'string' ? message : undefined;
};

const getShopifyOrdersDateRange = (
  value: ShopifyOrdersFilterState['createdAt'],
) => ({
  createdAtFrom: value?.[0]?.startOf('day').toUTC().toISO() ?? undefined,
  createdAtTo: value?.[1]?.endOf('day').toUTC().toISO() ?? undefined,
});

const getShopifyOrdersRequestFilters = (
  filterState: ShopifyOrdersFilterState,
) => {
  const prunedFilters = pruneFilterState(
    filterState,
  ) as ShopifyOrdersFilterState;
  const { createdAtFrom, createdAtTo } = getShopifyOrdersDateRange(
    prunedFilters.createdAt,
  );

  return {
    tags: prunedFilters.tags,
    fulfillmentStatuses: prunedFilters.fulfillmentStatuses,
    paymentStatuses: prunedFilters.paymentStatuses,
    deliveryMethods: prunedFilters.deliveryMethods,
    createdAtFrom,
    createdAtTo,
  };
};

const notifyFieldMappingsError = (
  self: IAnyStateTreeNode & {
    savingFieldMappingKey: string | null;
  },
  error: unknown,
): void => {
  const code = getGraphQLErrorCode(error);
  const message =
    code === 'SHOPIFY_FIELD_MAPPING_INVALID'
      ? getGraphQLErrorMessage(error) ??
        String(
          i18n.t(
            `${FIELD_MAPPINGS_TRANSLATION_PREFIX}.mandatoryMissingError`,
          ),
        )
      : String(i18n.t(`${FIELD_MAPPINGS_TRANSLATION_PREFIX}.saveError`));

  getAppEnv(self).notification({
    type: NotificationType.ERROR,
    id: 'shopify-field-mappings-save-error',
    message,
  });
};

const persistFieldMappings = async (
  self: IAnyStateTreeNode & {
    fieldMappings: ShopifyFieldMapping[];
  },
  fieldMappings: ShopifyFieldMapping[],
): Promise<ShopifyFieldMapping[]> => {
  const persistedFieldMappings = fieldMappings.filter(
    (mapping) => !(
      mapping.isCustom
      && mapping.shopifyFields.length === 0
    ),
  );

  const res = await getAppRoot(self).model.mutateUpdateShopifyConfiguration(
    {
      input: {
        fieldMappings: persistedFieldMappings.map((mapping) => ({
          routificField: mapping.routificField,
          shopifyFields: mapping.shopifyFields,
          isCustom: mapping.isCustom,
          label: mapping.label,
        })),
      },
    },
    ShopifyConfigurationSelector,
  ).currentPromise();

  return mapFieldMappings(
    res.updateShopifyConfiguration?.fieldMappings,
  );
};

const withOptimisticFieldMappingsPersist = flow(function* (
  self: IAnyStateTreeNode & {
    fieldMappings: ShopifyFieldMapping[];
    isSavingFieldMappings: boolean;
    savingFieldMappingKey: string | null;
  },
  routificField: string,
  nextFieldMappings: ShopifyFieldMapping[],
  previousFieldMappings: ShopifyFieldMapping[],
) {
  self.fieldMappings = nextFieldMappings;

  try {
    self.isSavingFieldMappings = true;
    self.savingFieldMappingKey = routificField;
    self.fieldMappings = yield persistFieldMappings(self, nextFieldMappings);

    return self.fieldMappings;
  } catch (error) {
    self.fieldMappings = previousFieldMappings;
    notifyFieldMappingsError(self, error);
    throw error;
  } finally {
    self.isSavingFieldMappings = false;
    self.savingFieldMappingKey = null;
  }
});

const Shopify = types.model('Shopify')
  .props({
    isLoading: types.optional(types.boolean, false),
    isLoadingInstallation: types.optional(types.boolean, false),
    isLoadingFieldMappings: types.optional(types.boolean, false),
    isLoadingOrders: types.optional(types.boolean, false),
    isImporting: types.optional(types.boolean, false),
    isLoadingAvailableFields: types.optional(types.boolean, false),
    isSavingFieldMappings: types.optional(types.boolean, false),
    isSavingTagDeliveredOrders: types.optional(types.boolean, false),
    isSavingFulfillmentOnDelivery: types.optional(types.boolean, false),
    isLoadingCustomFields: types.optional(types.boolean, false),
    hasFieldMappingsError: types.optional(types.boolean, false),
    hasInstallationError: types.optional(types.boolean, false),
    hasOrdersError: types.optional(types.boolean, false),
    hasAvailableFieldsError: types.optional(types.boolean, false),
    isOrdersModalOpen: types.optional(types.boolean, false),
    authorizeUrl: types.maybeNull(types.string),
    isConnected: types.optional(types.boolean, false),
    shopDomain: types.maybeNull(types.string),
    enabledBy: types.maybeNull(types.string),
    connectedAt: types.maybeNull(types.string),
    tagDeliveredOrders: types.optional(types.boolean, false),
    updateFulfillmentOnDelivery: types.optional(types.boolean, false),
    ordersSearchTerm: types.optional(types.string, ''),
    ordersFilters: types.optional(
      types.frozen<ShopifyOrdersFilterState>(),
      EMPTY_SHOPIFY_ORDERS_FILTERS,
    ),
  })
  .volatile(() => ({
    fieldMappings: [] as ShopifyFieldMapping[],
    availableShopifyFields: [] as ShopifyAvailableField[],
    customFieldOptions: [] as string[],
    savingFieldMappingKey: null as string | null,
    shopifyOrders: [] as ShopifyOrder[],
    /** Incremented on each orders fetch so overlapping requests don't apply stale results. */
    ordersFetchGeneration: 0,
    ordersPageInfo: createDefaultShopifyOrdersPageInfo(),
  }))
  .actions((self) => ({
    openOrdersModal() {
      self.isOrdersModalOpen = true;
    },
    setOrdersSearchTerm(term: string) {
      self.ordersSearchTerm = term;
    },
    setOrdersFilters(filters: ShopifyOrdersFilterState) {
      self.ordersFilters = filters;
    },
    clearOrdersFilters() {
      self.ordersFilters = EMPTY_SHOPIFY_ORDERS_FILTERS;
    },
    closeOrdersModal() {
      self.isOrdersModalOpen = false;
      self.ordersSearchTerm = '';
      self.clearOrdersFilters();
    },
    resetOrders() {
      self.shopifyOrders = [];
      self.ordersPageInfo = {
        hasNextPage: false,
        endCursor: null,
      };
      self.hasOrdersError = false;
    },
    startOAuthFlow: flow(function* (shopDomain: string) {
      try {
        self.isLoading = true;
        const res = yield getAppEnv(self).api.post('/oauth/shopify/authorize', { shopDomain });
        self.authorizeUrl = res.data.authorizeUrl ?? null;
      } finally {
        self.isLoading = false;
      }
    }),
    fetchInstallationPreview: flow(function* (linkToken: string) {
      const res = yield getAppEnv(self).api.get('/shopify/installations/preview', {
        params: { linkToken },
      });
      const data: { shopDomain: string } = res.data;
      return data;
    }),
    linkInstallation: flow(function* (linkToken: string) {
      const res = yield getAppEnv(self).api.post('/shopify/installations/link', {
        linkToken,
      });
      const data: { redirectTo: string } = res.data;
      return data;
    }),
    disconnectInstallation: flow(function* () {
      try {
        self.isLoading = true;
        yield getAppRoot(self).model.mutateDisableShopifyInstallation().currentPromise();
        self.isConnected = false;
        self.shopDomain = null;
        self.enabledBy = null;
        self.connectedAt = null;
        self.fieldMappings = [];
        self.availableShopifyFields = [];
        self.customFieldOptions = [];
        self.savingFieldMappingKey = null;
        self.resetOrders();
        self.hasFieldMappingsError = false;
        self.hasAvailableFieldsError = false;
      } finally {
        self.isLoading = false;
      }
    }),
    fetchInstallation: flow(function* () {
      try {
        self.isLoadingInstallation = true;
        self.hasInstallationError = false;
        const res = yield getAppRoot(self).model.queryGetShopifyInstallation(
          {},
          ShopifyInstallationQuery,
          { fetchPolicy: 'no-cache' },
        ).currentPromise();
        const installation = res.getShopifyInstallation;
        if (installation) {
          self.isConnected = installation.active ?? false;
          self.shopDomain = installation.shopDomain ?? null;
          self.enabledBy = installation.enabledBy ?? null;
          self.connectedAt = installation.createdAt ?? null;
        } else {
          self.isConnected = false;
          self.shopDomain = null;
          self.enabledBy = null;
          self.connectedAt = null;
        }
      } catch {
        self.isConnected = false;
        self.shopDomain = null;
        self.enabledBy = null;
        self.connectedAt = null;
        self.hasInstallationError = true;
      } finally {
        self.isLoadingInstallation = false;
      }
    }),
    fetchFieldMappings: flow(function* () {
      if (self.isLoadingFieldMappings) {
        return;
      }
      try {
        self.isLoadingFieldMappings = true;
        self.hasFieldMappingsError = false;
        self.fieldMappings = [];

        const res = yield getAppRoot(self).model.queryGetShopifyConfiguration(
          {},
          ShopifyConfigurationSelector,
          { fetchPolicy: 'no-cache' },
        ).currentPromise();

        self.fieldMappings = mapFieldMappings(
          res.getShopifyConfiguration?.fieldMappings,
        );
        self.tagDeliveredOrders =
          res.getShopifyConfiguration?.tagDeliveredOrders ?? false;
        self.updateFulfillmentOnDelivery =
          res.getShopifyConfiguration?.updateFulfillmentOnDelivery ?? false;
      } catch {
        self.hasFieldMappingsError = true;
      } finally {
        self.isLoadingFieldMappings = false;
      }
    }),
    setTagDeliveredOrders: flow(function* (enabled: boolean) {
      const previous = self.tagDeliveredOrders;
      self.isSavingTagDeliveredOrders = true;
      self.tagDeliveredOrders = enabled;
      try {
        const res = yield getAppRoot(self).model
          .mutateUpdateShopifyTagDeliveredOrders(
            { enabled },
            ShopifyConfigurationSelector,
          )
          .currentPromise();
        self.tagDeliveredOrders =
          res.updateShopifyTagDeliveredOrders?.tagDeliveredOrders ?? enabled;
      } catch {
        self.tagDeliveredOrders = previous;
        getAppEnv(self).notification({
          type: NotificationType.ERROR,
          id: 'shopify-delivery-status-sync-save-error',
          message: String(i18n.t('shopify:tagDeliveredOrders.tagSaveError')),
        });
      } finally {
        self.isSavingTagDeliveredOrders = false;
      }
    }),
    setUpdateFulfillmentOnDelivery: flow(function* (enabled: boolean) {
      const previous = self.updateFulfillmentOnDelivery;
      self.isSavingFulfillmentOnDelivery = true;
      self.updateFulfillmentOnDelivery = enabled;
      try {
        const res = yield getAppRoot(self).model
          .mutateUpdateShopifyFulfillmentOnDelivery(
            { enabled },
            ShopifyConfigurationSelector,
          )
          .currentPromise();
        self.updateFulfillmentOnDelivery =
          res.updateShopifyFulfillmentOnDelivery
            ?.updateFulfillmentOnDelivery ?? enabled;
      } catch {
        self.updateFulfillmentOnDelivery = previous;
        getAppEnv(self).notification({
          type: NotificationType.ERROR,
          id: 'shopify-fulfillment-status-sync-save-error',
          message: String(
            i18n.t('shopify:tagDeliveredOrders.fulfillSaveError'),
          ),
        });
      } finally {
        self.isSavingFulfillmentOnDelivery = false;
      }
    }),
    fetchAvailableShopifyFields: flow(function* () {
      if (self.isLoadingAvailableFields) {
        return;
      }

      try {
        self.isLoadingAvailableFields = true;
        self.hasAvailableFieldsError = false;
        self.availableShopifyFields = [];

        const res = yield getAppRoot(self).model.queryGetShopifyAvailableFields(
          {},
          ShopifyAvailableFieldsSelector,
          { fetchPolicy: 'no-cache' },
        ).currentPromise();

        self.availableShopifyFields = (res.getShopifyAvailableFields ?? []).map((field) => ({
          category: field.category,
          path: field.path,
          label: field.label,
        }));
      } catch {
        self.hasAvailableFieldsError = true;
      } finally {
        self.isLoadingAvailableFields = false;
      }
    }),
    fetchCustomFieldOptions: flow(function* () {
      if (self.isLoadingCustomFields) {
        return;
      }

      try {
        self.isLoadingCustomFields = true;
        const res = yield getAppRoot(self).model.queryCustomFields(
          {},
          selectFromCustomFieldPreferencesDto().customFields,
          { fetchPolicy: 'no-cache' },
        ).currentPromise();

        self.customFieldOptions = res.customFields?.customFields ?? [];
      } finally {
        self.isLoadingCustomFields = false;
      }
    }),
    ensureCustomField: flow(function* (label: string) {
      const trimmedLabel = label.trim();
      const existingLabel = self.customFieldOptions.find(
        (customField) => customField.toLowerCase() === trimmedLabel.toLowerCase(),
      );

      if (existingLabel) {
        return existingLabel;
      }

      const res = yield getAppRoot(self).model.mutateUpsertCustomFields(
        {
          customFieldPrefs: {
            customFields: [trimmedLabel],
          },
        },
        selectFromCustomFieldPreferencesDto().customFields,
      ).currentPromise();

      self.customFieldOptions = res.upsertCustomFields.customFields ?? self.customFieldOptions;

      return (
        res.upsertCustomFields.customFields?.find(
          (customField: string) => customField.toLowerCase() === trimmedLabel.toLowerCase(),
        ) ?? trimmedLabel
      );
    }),
    addLocalFieldMapping(mapping: ShopifyFieldMapping): AddLocalFieldMappingResult {
      const alreadyExists = self.fieldMappings.some(
        (existingMapping) => existingMapping.routificField === mapping.routificField,
      );

      if (alreadyExists) {
        return { ok: false, reason: 'duplicate' };
      }

      self.fieldMappings = [
        ...self.fieldMappings,
        {
          routificField: mapping.routificField,
          shopifyFields: [...mapping.shopifyFields],
          isCustom: mapping.isCustom,
          label: mapping.label,
        },
      ];

      return { ok: true };
    },
    saveFieldMapping: flow(function* (
      routificField: string,
      shopifyFields: string[],
      meta?: ShopifyFieldMappingMeta,
    ) {
      if (self.isSavingFieldMappings) {
        return self.fieldMappings;
      }

      const previousFieldMappings = cloneFieldMappings(self.fieldMappings);
      const existingMapping = previousFieldMappings.find(
        (mapping) => mapping.routificField === routificField,
      );
      const nextFieldMappings = existingMapping
        ? previousFieldMappings.map((mapping) =>
          mapping.routificField === routificField
            ? { ...mapping, shopifyFields: [...shopifyFields] }
            : mapping,
        )
        : [
          ...previousFieldMappings,
          {
            routificField,
            shopifyFields: [...shopifyFields],
            isCustom: meta?.isCustom,
            label: meta?.label,
          },
        ];

      return yield withOptimisticFieldMappingsPersist(
        self,
        routificField,
        nextFieldMappings,
        previousFieldMappings,
      );
    }),
    removeFieldMapping: flow(function* (routificField: string) {
      if (self.isSavingFieldMappings) {
        return self.fieldMappings;
      }

      const previousFieldMappings = cloneFieldMappings(self.fieldMappings);
      const nextFieldMappings = previousFieldMappings.filter(
        (mapping) => mapping.routificField !== routificField,
      );

      return yield withOptimisticFieldMappingsPersist(
        self,
        routificField,
        nextFieldMappings,
        previousFieldMappings,
      );
    }),
    resetFieldMappingsToDefault: flow(function* () {
      if (self.isSavingFieldMappings) {
        return self.fieldMappings;
      }

      try {
        self.isSavingFieldMappings = true;
        self.savingFieldMappingKey = null;

        const res = yield getAppRoot(self).model.mutateResetShopifyFieldMappings(
          {},
          ShopifyConfigurationSelector,
        ).currentPromise();

        self.fieldMappings = mapFieldMappings(
          res.resetShopifyFieldMappings?.fieldMappings,
        );

        return self.fieldMappings;
      } finally {
        self.isSavingFieldMappings = false;
        self.savingFieldMappingKey = null;
      }
    }),
    fetchShopifyOrders: flow(function* (
      projectId: string,
      cursor?: string | null,
      searchInput?: string,
      filtersInput?: ShopifyOrdersFilterState,
    ) {
      self.ordersFetchGeneration += 1;
      const fetchGeneration = self.ordersFetchGeneration;

      const searchForRequest =
        searchInput !== undefined
          ? (searchInput.trim() || undefined)
          : ((self.ordersSearchTerm ?? '').trim() || undefined);
      const requestFilters = getShopifyOrdersRequestFilters(
        filtersInput ?? self.ordersFilters ?? EMPTY_SHOPIFY_ORDERS_FILTERS,
      );

      try {
        self.isLoadingOrders = true;
        self.hasOrdersError = false;

        const res = yield getAppRoot(self).model.queryGetShopifyOrders(
          {
            projectId: Number(projectId),
            cursor: cursor ?? undefined,
            search: searchForRequest,
            tags: requestFilters.tags,
            fulfillmentStatuses: requestFilters.fulfillmentStatuses,
            paymentStatuses: requestFilters.paymentStatuses,
            deliveryMethods: requestFilters.deliveryMethods,
            createdAtFrom: requestFilters.createdAtFrom,
            createdAtTo: requestFilters.createdAtTo,
            limit: SHOPIFY_ORDERS_PAGE_SIZE,
          },
          ShopifyOrdersQuery,
          { fetchPolicy: 'no-cache' },
        ).currentPromise();

        if (fetchGeneration !== self.ordersFetchGeneration) {
          return;
        }

        const orders = res.getShopifyOrders?.orders ?? [];
        const pageInfo = res.getShopifyOrders?.pageInfo ?? {
          hasNextPage: false,
          endCursor: null,
        };

        if (!cursor) {
          self.shopifyOrders = orders.map((order: ShopifyOrder) => ({ ...order }));
        } else {
          const existingIds = new Set(self.shopifyOrders.map((order) => order.id));
          self.shopifyOrders = [
            ...self.shopifyOrders,
            ...orders
              .filter((order: ShopifyOrder) => !existingIds.has(order.id))
              .map((order: ShopifyOrder) => ({ ...order })),
          ];
        }

        self.ordersPageInfo = { ...pageInfo };
      } catch {
        if (fetchGeneration !== self.ordersFetchGeneration) {
          return;
        }
        self.hasOrdersError = true;
      } finally {
        if (fetchGeneration === self.ordersFetchGeneration) {
          self.isLoadingOrders = false;
        }
      }
    }),
    importSelectedOrders: flow(function* (projectId: string, shopifyOrderIds: string[]) {
      if (shopifyOrderIds.length === 0) {
        return;
      }

      try {
        self.isImporting = true;
        yield getAppEnv(self).message(shopifyImportLoadingMessage(projectId));

        const res = yield getAppRoot(self).model.mutateImportShopifyOrders(
          {
            projectId: Number(projectId),
            shopifyOrderIds,
          },
          ImportShopifyOrdersMutation,
        ).currentPromise();

        const result: ShopifyImportResult = {
          createdCount: res.importShopifyOrders.createdCount ?? 0,
          skippedCount: res.importShopifyOrders.skippedCount ?? 0,
          skippedOrders: (res.importShopifyOrders.skippedOrders ?? []).map(
            (order: SkippedShopifyOrder) => ({ ...order }),
          ),
        };

        yield getAppEnv(self).closeMessage(
          getShopifyImportLoadingMessageKey(projectId),
        );
        self.closeOrdersModal();

        if (result.createdCount > 0) {
          getAppRoot(self).account.trackUserForAnalytics(
            AnalyticsEvents.ORDERS_CREATED,
            {
              creationSource: CreationSource.SHOPIFY,
              importedCount: result.createdCount,
              skippedCount: result.skippedCount,
            },
          );
        }

        if (result.createdCount > 0 && result.skippedCount === 0) {
          yield getAppEnv(self).notification(
            shopifyImportSuccessNotification(result.createdCount),
          );
        } else if (result.createdCount > 0) {
          yield getAppEnv(self).notification(
            shopifyImportPartialSuccessNotification(
              result.createdCount,
              shopifyOrderIds.length,
              result.skippedOrders,
            ),
          );
        } else {
          yield getAppEnv(self).notification(
            shopifyImportAllSkippedNotification(result.skippedOrders),
          );
        }

        self.resetOrders();
        void getAppRoot(self).model.fetchUnscheduledVisitsLatLngs(projectId);
      } catch (error) {
        yield getAppEnv(self).closeMessage(
          getShopifyImportLoadingMessageKey(projectId),
        );
        self.closeOrdersModal();

        const code = getGraphQLErrorCode(error);
        if (code === 'SHOPIFY_CONNECTION_REVOKED') {
          self.isConnected = false;
          yield getAppEnv(self).notification(
            shopifyImportConnectionRevokedNotification(),
          );
        } else if (code === 'SHOPIFY_RATE_LIMITED') {
          yield getAppEnv(self).notification(
            shopifyImportRateLimitedNotification(),
          );
        } else {
          yield getAppEnv(self).notification(shopifyImportErrorNotification());
        }
      } finally {
        self.isImporting = false;
      }
    }),
  }));

export default Shopify;
